// ============================================
// COMPONENT - EditarUsuarioModal
// ============================================

import { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { useActualizarUsuario, useSucursales, useUsuarioPorId, useUIStore } from '@almacenes/core';
import type { UsuarioSucursal, ActualizarUsuarioDto } from '@almacenes/core';

interface EditarUsuarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  usuario: UsuarioSucursal | null;
}

export const EditarUsuarioModal = ({ isOpen, onClose, usuario }: EditarUsuarioModalProps) => {
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    sucursalId: '',
    password: '',
    repetirPassword: '',
  });

  const { data: sucursales, isLoading: isLoadingSucursales } = useSucursales();
  const { data: usuarioDetalle, isLoading: isLoadingUsuario } = useUsuarioPorId(usuario?.id || null);
  const actualizarUsuarioMutation = useActualizarUsuario();
  const addToast = useUIStore((state) => state.addToast);

  // Cargar datos del usuario cuando se obtienen los detalles
  useEffect(() => {
    if (usuarioDetalle && isOpen) {
      // Para vendedores, obtener la sucursal del array (solo habrá una)
      const sucursalId = usuarioDetalle.sucursales && usuarioDetalle.sucursales.length > 0 
        ? usuarioDetalle.sucursales[0].id.toString() 
        : '';
      
      setFormData({
        nombre: usuarioDetalle.nombre || '',
        apellidos: usuarioDetalle.apellidos || '',
        sucursalId: sucursalId,
        password: '',
        repetirPassword: '',
      });
    }
  }, [usuarioDetalle, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!usuario || !usuarioDetalle) return;

    // Validación
    if (!formData.nombre.trim()) {
      addToast('error', 'El nombre es obligatorio');
      return;
    }
    if (!formData.apellidos.trim()) {
      addToast('error', 'Los apellidos son obligatorios');
      return;
    }
    if (!formData.password.trim()) {
      addToast('error', 'La contraseña es obligatoria');
      return;
    }
    if (formData.password.length < 8) {
      addToast('error', 'La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (formData.password !== formData.repetirPassword) {
      addToast('error', 'Las contraseñas no coinciden');
      return;
    }
    
    // Si el usuario NO es propietario, debe seleccionar una sucursal
    if (!usuarioDetalle.esPropietario && !formData.sucursalId) {
      addToast('error', 'Debe seleccionar una sucursal');
      return;
    }

    const datos: ActualizarUsuarioDto = {
      usuarioId: usuarioDetalle.id,
      nombre: formData.nombre.trim(),
      apellidos: formData.apellidos.trim(),
      sucursalId: usuarioDetalle.esPropietario ? null : Number(formData.sucursalId),
      password: formData.password.trim(),
    };

    try {
      await actualizarUsuarioMutation.mutateAsync(datos);
      addToast('success', 'Usuario actualizado correctamente');
      handleClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al actualizar el usuario';
      addToast('error', errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({
      nombre: '',
      apellidos: '',
      sucursalId: '',
      password: '',
      repetirPassword: '',
    });
    onClose();
  };

  if (!isOpen || !usuario) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            Editar Usuario
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: '#11194F' }}
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Loading State */}
        {isLoadingUsuario ? (
          <div className="p-12 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
            <p className="mt-4 text-gray-600">Cargando datos del usuario...</p>
          </div>
        ) : !usuarioDetalle ? (
          <div className="p-12 text-center text-red-500">
            Error al cargar los datos del usuario
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {/* Usuario actual info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Editando usuario:{' '}
                <span className="font-semibold" style={{ color: '#11194F' }}>
                  {usuarioDetalle.nombre} {usuarioDetalle.apellidos}
                </span>
                {usuarioDetalle.esPropietario && (
                  <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    Propietario
                  </span>
                )}
              </p>
            </div>

          <div className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ingrese el nombre"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43BFFF] transition-all"
              />
            </div>

            {/* Apellidos */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Apellidos <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.apellidos}
                onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                placeholder="Ingrese los apellidos"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43BFFF] transition-all"
              />
            </div>

            {/* Sucursal - Solo si NO es propietario */}
            {!usuarioDetalle.esPropietario && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                  Sucursal <span className="text-red-500">*</span>
                </label>
                {isLoadingSucursales ? (
                  <div className="text-sm text-gray-500">Cargando sucursales...</div>
                ) : (
                  <select
                    value={formData.sucursalId}
                    onChange={(e) => setFormData({ ...formData, sucursalId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43BFFF] transition-all"
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales
                      ?.filter((s) => s.activo)
                      .map((sucursal) => (
                        <option key={sucursal.id} value={sucursal.id}>
                          {sucursal.nombre} - {sucursal.direccion} #{sucursal.numeroDireccion}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            )}

            {/* Nueva Contraseña */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Nueva Contraseña <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43BFFF] transition-all"
              />
              <p className="mt-1 text-xs text-gray-500">
                La contraseña debe tener al menos 8 caracteres
              </p>
            </div>

            {/* Repetir Contraseña */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Repetir Contraseña <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.repetirPassword}
                onChange={(e) => setFormData({ ...formData, repetirPassword: e.target.value })}
                placeholder="Repita la contraseña"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43BFFF] transition-all"
              />
              {formData.password &&
                formData.repetirPassword &&
                formData.password !== formData.repetirPassword && (
                  <p className="mt-1 text-xs text-red-500">Las contraseñas no coinciden</p>
                )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg font-medium transition-colors hover:bg-gray-50"
              style={{ color: '#11194F' }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={actualizarUsuarioMutation.isPending}
              className="px-6 py-2 bg-[#43BFFF] text-white rounded-lg font-medium transition-colors hover:bg-[#3AABEB] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {actualizarUsuarioMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-solid border-current border-r-transparent" />
                  Actualizando...
                </>
              ) : (
                'Actualizar'
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
